import React from 'react';
import { Box, Tab, Chip, Collapse, Paper, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import { StyledTabs, TabsWrapper } from 'components/LMA/LMA.style';
import GuidanceButton from './GuidanceButton';
import { Field, RichText as SitecoreRichText } from '@sitecore-jss/sitecore-jss-nextjs';

interface DynamicTab {
  label: string;
  value: number;
  dataKey: string;
}

interface TabBarProps {
  tabs: DynamicTab[];
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  allTabTotals: Record<string, number>;
  disabled?: boolean;
  guidanceButtonText?: Field<string>;
  guidanceTitle?: Field<string>;
  guidanceBody?: Field<string>;
  /** Rendered left of the Guidance control (e.g. Processed tab view toggle). */
  trailingActions?: React.ReactNode;
}

// Icon mapping for each tab by dataKey
const TAB_ICONS: Record<string, JSX.Element> = {
  referrals: <AssignmentIcon sx={{ fontSize: 18 }} key="referrals" />,
  nonReferrals: <PersonOffIcon sx={{ fontSize: 18 }} key="non-referrals" />,
  processedReferrals: <CheckCircleIcon sx={{ fontSize: 18 }} key="processed-referrals" />,
  flaggedRecords: <FlagIcon sx={{ fontSize: 18 }} key="flagged-records" />,
};

const getTabIcon = (dataKey: string) => TAB_ICONS[dataKey] || null;

// Reusable chip styling function
const getChipStyles = (isActive: boolean) => ({
  height: 20,
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: isActive
    ? 'rgba(255, 255, 255, 0.8)'
    : (theme: any) => theme.palette.neutral4?.main,
  color: isActive ? 'rgba(0, 0, 0, 0.8)' : (theme: any) => theme.palette.neutral7?.main,
  transition: 'all 0.3s ease',
});

// Reusable tab label component
const TabLabel: React.FC<{ label: string; count: number; isActive: boolean }> = ({
  label,
  count,
  isActive,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <span>{label}</span>
    {count > 0 && <Chip label={count} size="small" sx={getChipStyles(isActive)} />}
  </Box>
);

// Tab group component
const TabGroup: React.FC<{
  tabs: DynamicTab[];
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  allTabTotals: Record<string, number>;
  startIndex: number;
  disabled?: boolean;
}> = ({ tabs, activeTab, onTabChange, allTabTotals, startIndex, disabled = false }) => {
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (disabled) return;
    // Find the tab by its position in the tabs array and use its value property
    const selectedTab = tabs[newValue];
    if (selectedTab) {
      onTabChange(event, selectedTab.value);
    }
  };

  // Find the active tab index in the current tab group
  const activeTabIndex = tabs.findIndex((tab) => tab.value === activeTab);

  return (
    <StyledTabs
      value={activeTabIndex >= 0 ? activeTabIndex : false}
      onChange={handleChange}
      aria-label={`${startIndex === 0 ? 'referral' : 'user'} tabs`}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;
        const count = (allTabTotals && allTabTotals[tab.dataKey]) || 0;

        return (
          <Tab
            key={tab.value}
            icon={getTabIcon(tab.dataKey)}
            iconPosition="start"
            disabled={disabled && activeTab !== tab.value}
            sx={{
              ...(disabled && activeTab !== tab.value
                ? { pointerEvents: 'none', opacity: 0.5 }
                : {}),
            }}
            label={<TabLabel label={tab.label} count={count} isActive={isActive} />}
          />
        );
      })}
    </StyledTabs>
  );
};

const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  allTabTotals,
  disabled = false,
  guidanceButtonText,
  guidanceTitle,
  guidanceBody,
  trailingActions,
}) => {
  const [guidanceOpen, setGuidanceOpen] = React.useState(false);

  // Default values if Sitecore fields are not provided
  const defaultGuidanceTitle = 'Page Guidance';
  const defaultGuidanceBody = {
    value:
      '<p>Welcome to the Referral Management System. This page helps you manage patient referrals and registrations efficiently.</p>',
  };

  return (
    <Box>
      {/* Main Tab Groups */}
      <TabsWrapper>
        <TabGroup
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
          allTabTotals={allTabTotals}
          startIndex={0}
          disabled={disabled}
        />

        {/* Optional actions + Guidance */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {trailingActions}
          <GuidanceButton
            guidanceOpen={guidanceOpen}
            onToggleGuidance={() => setGuidanceOpen(!guidanceOpen)}
            buttonText={guidanceButtonText}
          />
        </Box>
      </TabsWrapper>

      {/* Guidance Content */}
      <Collapse in={guidanceOpen}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 1.5,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 1.5, color: (theme: any) => theme.palette.info?.dark }}
          >
            {guidanceTitle?.value || defaultGuidanceTitle}
          </Typography>

          {/* Render Sitecore Rich Text or default content */}
          <Box>
            {guidanceBody?.value ? (
              <SitecoreRichText field={guidanceBody} />
            ) : (
              <SitecoreRichText field={defaultGuidanceBody} />
            )}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default TabBar;
